import {AbstractControl, FormBuilder, FormGroup} from "@angular/forms";
import {Observable} from "rxjs";
import {merge} from "rxjs/internal/observable/merge";
import {map} from "rxjs/operators";
import {ObservableBaseForm} from "../../shared/forms/observable-base.form";
import {ContentSearchParamsModel} from "../models/search-params/content-search-params.model";
import {ContentSearchConfigService} from "../services/content-search-config.service";
import {ContentSearchParamsFactoryService} from "../services/factories/content-search-params-factory.service";
import {SearchScopeEnum} from "../../shared/enums/search-scope.enum";

export class ContentSearchForm extends ObservableBaseForm<ContentSearchParamsModel> {
    public get searchText(): AbstractControl {
        return this.formInstance.get("searchText");
    }

    public get searchScope(): AbstractControl {
        return this.formInstance.get("searchScope");
    }

    public get pageNumber(): AbstractControl {
        return this.formInstance.get("pageNumber");
    }

    public get filter(): AbstractControl {
        return this.formInstance.get("filter");
    }

    public static createForm(
        formBuilder: FormBuilder, initialValues: ContentSearchParamsModel,
        searchParamsFactoryService: ContentSearchParamsFactoryService,
        configService: ContentSearchConfigService
    ): ContentSearchForm {

        const form = formBuilder.group({
            searchText: [initialValues.searchText],
            searchScope: [initialValues.searchScope],
            pageNumber: [initialValues.pageNumber],
            filter: [initialValues.filter]
        });

        return new ContentSearchForm(form, initialValues, formBuilder, searchParamsFactoryService, configService);
    }

    public constructor(
        formInstance: FormGroup,
        initialValues: ContentSearchParamsModel,
        private formBuilder: FormBuilder,
        private searchParamsFactoryService: ContentSearchParamsFactoryService,
        private configService: ContentSearchConfigService
    ) {
        super(formInstance, initialValues);
        merge(
            this.changeObservable,
            this.submitObservable
        )
        .subscribe(model => {
            // sync form & params state
            this.formInstance.patchValue(model, {emitEvent: false});
            this.initialValues = model;
        });
    }

    public reset(): void {
        this.searchText.setValue("", {emitEvent: false});
        this.searchScope.setValue(this.configService.getDefaultScope(), {emitEvent: false});
        this.pageNumber.setValue(1, {emitEvent: false});
        this.filter.setValue(this.searchParamsFactoryService.createFilter(this.searchScope.value), {emitEvent: false});
    }

    protected createChangeSubscription(): Observable<ContentSearchParamsModel> {
       return merge(
            this.searchText.valueChanges.pipe(
                map((searchText: string) => ({
                        searchText: searchText,
                        pageNumber: 1,
                        filter: this.searchParamsFactoryService.createFilter(this.searchScope.value)
                    }
                ))
            ),
            this.searchScope.valueChanges.pipe(
                map((searchScope: string) => ({
                        searchScope: searchScope,
                        searchText: this.initialValues.searchText, // restore the last submitted search text
                        pageNumber: 1,
                        filter: this.searchParamsFactoryService.createFilter(searchScope as SearchScopeEnum)
                    }
                ))
            ),
            this.filter.valueChanges.pipe(
                map((filter: string) => ({
                        filter: filter,
                        searchText: this.initialValues.searchText, // restore the last submitted search text
                        pageNumber: 1
                    }
                ))
            ),
            this.pageNumber.valueChanges.pipe(
                map((pageNumber: string) => ({
                        pageNumber: pageNumber,
                        searchText: this.initialValues.searchText // restore the last submitted search text
                    }
                ))
            )
        )
        .pipe(
            map(partialModel => this.getModel(partialModel))
        );
    }
}
